// Considered: `ContextFilterBarMolecule` — add a "shared" state to it (see
// spec Reuse Audit, specs/shareable-context-links.md). Rejected: the bar's
// job is to show and edit YOUR filter state; shared-link mode is the state in
// which that is precisely what is not happening. It is also the only element
// that must render when the bar itself does not (a link carrying filters on
// a board where the recipient has none).
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom, { type MessageSeverity } from '../../atoms/Message/MessageAtom';
import './SharedLinkBannerMolecule.scss';

/**
 * What, if anything, is wrong with the shared link's `?view=`/`?f=` payload
 * relative to THIS context — computed by the caller (`TreeMapOrganism`),
 * which is the only place that has both the decoded envelope and the
 * context/view data to judge it against.
 */
export type SharedLinkBannerNotice = 'none' | 'viewUnresolved' | 'viewUnavailable' | 'noLevelMatch';

export interface SharedLinkBannerMoleculeProps {
  /** True when the payload contains a filter-variable token (`@me`, ...) — the results are the VIEWER's own items, not the sender's exact rows. */
  hasVariableToken: boolean;
  /**
   * `'none'` renders the plain active-link message; any other value replaces it
   * with the degraded-state message. It does NOT displace the `@`-token warning
   * — the two facts are independent and both load-bearing, so they compose
   * (R-2). See the `variableTokenNote` render below.
   */
  notice: SharedLinkBannerNotice;
  /** Drops `?f=` and restores the recipient's own layers. Never disabled — see D-3. */
  onExit: () => void;
  /**
   * Re-attempts the `?view=` lookup. Wired by the host only for the transient
   * `viewUnavailable` state (D-4: an outage is not a refusal), which is the
   * only notice a retry can clear — 403/404 (`viewUnresolved`) will answer the
   * same way however many times it is asked. Absent ⇒ no retry control.
   */
  onRetry?: () => void;
  /**
   * True while a retry is in flight. The refetch is deliberately a BACKGROUND
   * one — it must not blank the board the recipient is reading (EAINT-793 R-7)
   * — which leaves the press with no visible consequence unless the control
   * says so itself. Drives the button's busy state; the button stays mounted
   * throughout, so focus is never dropped mid-attempt.
   */
  retrying?: boolean;
  className?: string;
}

/**
 * "Viewing a shared filter set · Exit" (or the `@`-token / degraded variant,
 * which compose rather than displace one another — R-2).
 * Renders only while a link is active. Per D-3, collapsing (the compact
 * affordance below) is the only way to shrink this — there is no way to
 * reduce the indication to zero while `?f=` is active; `onExit` stays
 * reachable in both states.
 */
const SharedLinkBannerMolecule: React.FC<SharedLinkBannerMoleculeProps> = ({
  hasVariableToken,
  notice,
  onExit,
  onRetry,
  retrying = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  // A degraded `notice` wins the primary slot; the variable-token wording is the
  // fallback for an otherwise-healthy link. Table + lookup rather than a
  // four-deep ternary, so adding a notice is one row.
  const MESSAGE_KEY_BY_NOTICE: Record<string, string> = {
    viewUnresolved: 'columnFilters.sharedLink.viewUnresolved',
    viewUnavailable: 'columnFilters.sharedLink.viewUnavailable',
    noLevelMatch: 'columnFilters.sharedLink.noLevelMatch',
  };
  const fallbackMessageKey = hasVariableToken
    ? 'columnFilters.sharedLink.activeVariableToken'
    : 'columnFilters.sharedLink.active';
  const messageKey =
    (notice ? MESSAGE_KEY_BY_NOTICE[notice] : undefined) ?? fallbackMessageKey;

  /**
   * R-2 — a degraded notice takes the primary slot, so the `@`-token fact needs
   * its own line rather than being dropped: with `noLevelMatch` + an `@me`
   * payload the recipient was told the link does not match this context and
   * never told the rows resolve against THEIR identity. Silent divergence is
   * the failure mode the spec calls "the worse of the two". Rendered as a
   * second message rather than appended text — building one sentence out of two
   * translated fragments is not translatable.
   *
   * Only when a notice occupies the primary slot; at `notice === 'none'` the
   * primary message is already the `activeVariableToken` variant.
   */
  const showVariableTokenNote = hasVariableToken && notice !== 'none';

  // Retry answers only the transient state — see the prop doc.
  const showRetry = notice === 'viewUnavailable' && onRetry !== undefined;

  const severity: MessageSeverity = notice === 'none' ? 'info' : 'warn';

  if (collapsed) {
    return (
      <div className={`shared-link-banner shared-link-banner--collapsed ${className}`.trim()}>
        <ButtonAtom
          variant="text"
          size="small"
          icon="pi pi-link"
          label={t('columnFilters.sharedLink.compactLabel')}
          aria-label={t('columnFilters.sharedLink.expand')}
          onClick={() => setCollapsed(false)}
          className="shared-link-banner__expand"
        />
        <ButtonAtom
          variant="text"
          size="small"
          label={t('columnFilters.sharedLink.exit')}
          onClick={onExit}
          className="shared-link-banner__exit"
        />
      </div>
    );
  }

  return (
    <div className={`shared-link-banner ${className}`.trim()}>
      <div className="shared-link-banner__messages">
        <MessageAtom severity={severity} text={t(messageKey)} className="shared-link-banner__message" />
        {showVariableTokenNote && (
          <MessageAtom
            severity="info"
            text={t('columnFilters.sharedLink.variableTokenNote')}
            className="shared-link-banner__message shared-link-banner__message--token"
          />
        )}
      </div>
      <div className="shared-link-banner__actions">
        {showRetry && (
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-refresh"
            label={t('common.retry')}
            onClick={onRetry}
            // `loading` renders the spinner AND disables the control, so the
            // in-flight attempt cannot be stacked on by a second press. Kept
            // mounted rather than swapped for a separate spinner element:
            // unmounting it would drop keyboard/AT focus to `<body>` with no
            // announcement, which is half of what R-7 was.
            loading={retrying}
            aria-busy={retrying}
            className="shared-link-banner__retry"
          />
        )}
        <ButtonAtom
          variant="text"
          size="small"
          label={t('columnFilters.sharedLink.exit')}
          onClick={onExit}
          className="shared-link-banner__exit"
        />
        <ButtonAtom
          variant="text"
          size="small"
          icon="pi pi-chevron-up"
          aria-label={t('columnFilters.sharedLink.collapse')}
          onClick={() => setCollapsed(true)}
          className="shared-link-banner__collapse"
        />
      </div>
    </div>
  );
};

export default SharedLinkBannerMolecule;
